// Service-auth token minting (szzt-spec.md §10.1) — the credential a publisher presents to a
// GATEWAY, as opposed to the PDS session every other module here uses.
//
// A gateway write surface (§11.1 slug allocation, §13 pin) is authenticated by a short-lived atproto
// service-auth JWT: signed by the account's repo signing key, addressed to the gateway's service DID
// (`aud`) and scoped to one lexicon method (`lxm`, which the gateway's `ServiceAuthVerifier` enforces).
// Minting one is a PDS call, so it belongs in this package — the only one that depends on
// `@atproto/api`. Callers that speak to a gateway (the `szzt` CLI) take the {@link ServiceAuthMinter}
// seam instead of an agent, so their own tests need no network library.
import type { Agent } from "@atproto/api";

/** What a service-auth token is minted for: an audience, a method scope, and an optional expiry. */
export interface ServiceAuthRequest {
  /** The gateway service DID the token is addressed to (`aud`), e.g. `did:web:szzt.app`. */
  aud: string;
  /** The lexicon method the token is scoped to (`lxm`). A gateway rejects a token minted for another. */
  lxm: string;
  /** Absolute expiry, Unix seconds. Omitted leaves the PDS's own short default in force. */
  exp?: number;
}

/**
 * Mints service-auth JWTs for the authenticated account (§10.1). A seam, not a class: a caller that
 * only ever talks to a gateway depends on this one method rather than a whole agent, and can be
 * tested against a fake that returns a fixed string.
 */
export interface ServiceAuthMinter {
  /** Mint a token for one gateway request. Rejects if the PDS refuses to sign. */
  mintServiceAuth(req: ServiceAuthRequest): Promise<string>;
}

/**
 * Wrap an authenticated {@link Agent} as a {@link ServiceAuthMinter} via
 * `com.atproto.server.getServiceAuth`. An app-password session can mint these — no OAuth is required.
 *
 * Typed to just the `com` namespace for the same reason as `atpClient`: both an OAuth `Agent` and an
 * app-password `AtpAgent` then flow in with no cast.
 */
export function atpServiceAuthMinter(agent: Pick<Agent, "com">): ServiceAuthMinter {
  return {
    async mintServiceAuth({ aud, lxm, exp }) {
      const res = await agent.com.atproto.server.getServiceAuth({
        aud,
        lxm,
        ...(exp !== undefined ? { exp } : {}),
      });
      return res.data.token;
    },
  };
}
